import { NAME_LISTS, type NamesDef } from '../names/types';
import type { Bundle } from './types';
import type { Err } from './common';

/*
 * The `names` section (CONTRACT §13.5): a port of packages/shared-cs/Runtime/Content/ContentValidator.Names.cs with the same messages
 * in the same order. Change both together; parity is pinned by validate/validate.skills.test.ts ("names") against fixtures/ot-parity.json.
 * - male, female and last each non-empty;
 * - every entry one capitalised word of letters, with single apostrophes or hyphens between letters ("O'Neil", "Mary-Lou");
 * - unique within its list (case-sensitive).
 * An absent section is the C# default (three empty lists, so three errors); an explicit null is "Bundle is missing 'names'.".
 */

/** `NamePattern`: a capital letter, then letters, with single apostrophes or hyphens between letters. */
export const NAME_PATTERN = /^[A-Z][A-Za-z]*(?:['-][A-Za-z]+)*$/;

/** One list's problems as `[path, message]`, in the server's order (the names form shows them per row). */
export function nameListProblems(
  key: string,
  list: (string | null)[] | null | undefined,
): [string, string][] {
  const w = `names.${key}`;
  if (list == null || list.length === 0) return [[key, `${w} needs at least one name.`]];
  const out: [string, string][] = [];
  const seen = new Set<string>();
  list.forEach((raw, i) => {
    const n = raw ?? '';
    const at = `${key}.${i}`;
    if (n.trim().length === 0) {
      out.push([at, `${w} has an empty name (#${i + 1}).`]);
      return;
    }
    if (!NAME_PATTERN.test(n))
      out.push([
        at,
        `${w} name '${n}' must be one capitalised word: letters, apostrophes and hyphens only.`,
      ]);
    if (seen.has(n)) out.push([at, `${w} has '${n}' twice.`]);
    else seen.add(n);
  });
  return out;
}

export function validateNames(b: Bundle, err: Err) {
  if (b.names === null) {
    err('names', undefined, '', "Bundle is missing 'names'.");
    return;
  }
  const names: NamesDef = b.names ?? {};
  for (const key of NAME_LISTS)
    for (const [path, m] of nameListProblems(key, names[key])) err('names', undefined, path, m);
}
